import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import { chatApi, type ChatGif } from "@/services/chat-api.service";

function parseTags(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export default function ChatGifsAdminPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [tagFilter, setTagFilter] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [editTags, setEditTags] = useState<Record<string, string>>({});
  const [deactivateId, setDeactivateId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin-chat-gifs", tagFilter],
    queryFn: () =>
      chatApi.listGifs(tagFilter.trim() || undefined, { includeInactive: true }),
  });

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Выберите файл GIF");
      return chatApi.uploadGif(file, parseTags(uploadTags));
    },
    onSuccess: () => {
      toast({ title: "GIF загружен" });
      setFile(null);
      setUploadTags("");
      void qc.invalidateQueries({ queryKey: ["admin-chat-gifs"] });
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка загрузки", description: e.message, variant: "destructive" });
    },
  });

  const patchMut = useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      chatApi.updateGif(id, { tags }),
    onSuccess: () => {
      toast({ title: "Теги обновлены" });
      void qc.invalidateQueries({ queryKey: ["admin-chat-gifs"] });
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка", description: e.message, variant: "destructive" });
    },
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => chatApi.deactivateGif(id),
    onSuccess: () => {
      toast({ title: "GIF деактивирован" });
      setDeactivateId(null);
      void qc.invalidateQueries({ queryKey: ["admin-chat-gifs"] });
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка", description: e.message, variant: "destructive" });
    },
  });

  const items = useMemo(() => list.data ?? [], [list.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-5 w-5 text-muted-foreground" aria-hidden />
        <h3 className="text-lg font-medium">Каталог GIF чата</h3>
        <SectionHelpTooltip label="Справка: каталог GIF">
          <p className="mb-2">
            Локальный каталог анимаций для модуля Чат (без Giphy/Tenor). Нужно право{" "}
            <code className="text-xs">chat.manage_gifs</code>.
          </p>
          <ul className="list-disc space-y-1 pl-4 text-sm">
            <li>Загрузка файла + теги через запятую</li>
            <li>Правка тегов и деактивация записи</li>
            <li>Деактивированные скрыты из пикера оператора</li>
          </ul>
        </SectionHelpTooltip>
      </div>

      <div className="rounded-md border p-4 space-y-3 max-w-xl">
        <Label htmlFor="chat-gif-file">Загрузка</Label>
        <Input
          id="chat-gif-file"
          type="file"
          accept="image/gif"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Input
          placeholder="теги через запятую"
          value={uploadTags}
          onChange={(e) => setUploadTags(e.target.value)}
        />
        <Button
          type="button"
          disabled={!file || uploadMut.isPending}
          onClick={() => uploadMut.mutate()}
        >
          <Upload className="mr-2 h-4 w-4" />
          {uploadMut.isPending ? "Загрузка…" : "Загрузить"}
        </Button>
      </div>

      <div className="flex items-end gap-2 max-w-md">
        <div className="flex-1 space-y-1">
          <Label htmlFor="chat-gif-filter">Фильтр по тегу</Label>
          <Input
            id="chat-gif-filter"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="например react"
          />
        </div>
        <Button type="button" variant="outline" onClick={() => void list.refetch()}>
          Обновить
        </Button>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
      {list.isError && (
        <p className="text-sm text-destructive">Не удалось загрузить каталог</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((g: ChatGif) => {
          const tagsValue = editTags[g.id] ?? (g.tags ?? []).join(", ");
          return (
            <div key={g.id} className="rounded-md border p-3 space-y-2">
              <div className="aspect-video overflow-hidden rounded bg-muted">
                {g.is_active ? (
                  <img
                    src={chatApi.gifContentUrl(g.id)}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                    неактивен
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {(g.tags ?? []).map((t) => (
                  <Badge key={t} variant="secondary">
                    {t}
                  </Badge>
                ))}
                {!g.is_active && <Badge variant="outline">inactive</Badge>}
              </div>
              <Input
                value={tagsValue}
                onChange={(e) =>
                  setEditTags((prev) => ({ ...prev, [g.id]: e.target.value }))
                }
                placeholder="теги"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={patchMut.isPending}
                  onClick={() =>
                    patchMut.mutate({ id: g.id, tags: parseTags(tagsValue) })
                  }
                >
                  Сохранить теги
                </Button>
                {g.is_active && (
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeactivateId(g.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!list.isLoading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">Каталог пуст</p>
      )}

      <AlertDialog
        open={!!deactivateId}
        onOpenChange={(open) => !open && setDeactivateId(null)}
      >
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Деактивировать GIF?</AlertDialogTitle>
            <AlertDialogDescription>
              Запись исчезнет из пикера операторов чата. Действие для каталога необратимо в
              этом UI (повторная загрузка файла создаст новую запись).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateMut.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deactivateMut.isPending || !deactivateId}
              onClick={(e) => {
                e.preventDefault();
                if (deactivateId) deactivateMut.mutate(deactivateId);
              }}
            >
              {deactivateMut.isPending ? "Деактивация…" : "Деактивировать"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
